import { Suspense, useMemo } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import GameGrid from '../components/GameGrid'
import GameTelemetry from '../components/GameTelemetry'
import ChallengeShare from '../components/ChallengeShare'
import { categoryInfo } from '../data/categories'
import { editorialByGame } from '../data/editorial'
import { findGame } from '../data/games'
import { useI18n } from '../i18n'
import { useSeo } from '../lib/seo'
import { isDateKey } from '../lib/dailyChallenge'

export default function GamePage() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { lang, t } = useI18n()
  const game = findGame(id)
  const category = game ? categoryInfo[game.category] : undefined
  const editorial = game ? editorialByGame[game.id] : undefined
  const challengeKey = isDateKey(searchParams.get('challenge')) ? searchParams.get('challenge')! : undefined
  const title = game ? (lang === 'zh' ? `${game.title.zh} – 免费在线${category?.name.zh} | PlayFree` : `${game.title.en} – Free Online ${category?.name.en} | PlayFree`) : 'Game not found'
  const description = game ? (lang === 'zh' ? `${game.description.zh} 免费在线玩，无需下载或注册，支持手机和电脑浏览器。` : `${game.description.en} Play free online with no download or sign-up, on mobile and desktop browsers.`) : 'Free browser games'
  const jsonLd = useMemo(() => game && editorial ? {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': ['VideoGame', 'WebApplication'],
        name: game.title[lang],
        description,
        url: `https://www.plays100.com/game/${game.id}${lang === 'zh' ? '?lang=zh' : ''}`,
        gamePlatform: ['Web Browser', 'Mobile Web'],
        applicationCategory: 'GameApplication',
        operatingSystem: 'Any',
        browserRequirements: 'Requires JavaScript and a modern web browser',
        datePublished: game.released,
        dateModified: editorial.reviewed,
        author: { '@type':'Person', name:'Mike', url:'https://www.plays100.com/about' },
        publisher: { '@type':'Organization', name:'PlayFree', url:'https://www.plays100.com/' },
        genre: category?.name[lang],
        isAccessibleForFree: true,
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: lang === 'zh' ? '首页' : 'Home', item: 'https://www.plays100.com/' },
          { '@type': 'ListItem', position: 2, name: category?.name[lang], item: `https://www.plays100.com/games/${game.category}` },
          { '@type': 'ListItem', position: 3, name: game.title[lang], item: `https://www.plays100.com/game/${game.id}` },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: game.faq[lang].map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    ],
  } : undefined, [game, editorial, lang, description, category])

  useSeo(title, description, game ? `/game/${game.id}` : `/game/${id??'missing'}`, lang, jsonLd, {noindex:!game})

  if (!game) return <div className="center"><h1>Game not found</h1><Link className="btn" to="/games">{t('games')}</Link></div>
  const Game = game.component
  return <article>
    <nav className="crumbs" aria-label="Breadcrumb"><Link to="/">{lang==='zh'?'首页':'Home'}</Link><span>›</span><Link to={`/games/${game.category}`}>{category?.name[lang]}</Link><span>›</span><span>{game.title[lang]}</span></nav>
    <div className="stage"><GameTelemetry gameId={game.id} challengeKey={challengeKey}><Suspense fallback={<div className="loading">···</div>}><Game /></Suspense></GameTelemetry></div>
    <header className="game-head">
      <span className="game-icon" style={{ background: `linear-gradient(150deg,${game.colors[0]},${game.colors[1]})` }}>{game.icon}</span>
      <div><h1>{game.title[lang]}</h1><p>{game.description[lang]}</p></div>
    </header>
    <div className="game-tags"><Link to={`/games/${game.category}`}>{category?.icon} {category?.name[lang]}</Link><span>{game.released?'✦ '+(lang==='zh'?'PlayFree 原创':'PlayFree original'):'◈ '+(lang==='zh'?'经典规则实现':'Classic-rule edition')}</span><span>✓ {lang==='zh'?'免费在线玩':'Free to play'}</span><span>✓ {lang==='zh'?'手机和电脑':'Mobile & desktop'}</span></div>
    <p className="game-byline">{lang==='zh'?<>由 <Link to="/about">Mike / PlayFree</Link> 设计、撰写并测试</>:<>Designed, written and tested by <Link to="/about">Mike / PlayFree</Link></>}</p>
    <ChallengeShare game={game} challengeKey={challengeKey}/>
    {editorial&&<section className="game-editorial"><header><div><small>{editorial.kind==='original'?(lang==='zh'?'原创设计记录':'ORIGINAL DESIGN NOTE'):(lang==='zh'?'经典规则实现说明':'CLASSIC IMPLEMENTATION NOTE')}</small><h2>{lang==='zh'?'这款游戏的独立价值':'What is distinct about this game'}</h2></div><dl>{game.released&&<div><dt>{lang==='zh'?'发布':'Released'}</dt><dd>{game.released}</dd></div>}<div><dt>{lang==='zh'?'最近审核':'Reviewed'}</dt><dd>{editorial.reviewed}</dd></div></dl></header><p>{editorial.design[lang]}</p><h3>{lang==='zh'?'我们检查了什么':'What we tested'}</h3><p>{editorial.qa[lang]}</p><Link to="/journal">{lang==='zh'?'查看全部开发与测试记录 →':'See every development and test note →'}</Link></section>}
    <section className="howto">
      <h2>{t('how')}</h2>
      <dl><div><dt>💻 {t('desktop')}</dt><dd>{game.desktop[lang]}</dd></div><div><dt>📱 {t('mobile')}</dt><dd>{game.mobile[lang]}</dd></div></dl>
    </section>
    <section className="game-guide">
      <h2>{lang === 'zh' ? `${game.title.zh} 游戏规则` : `${game.title.en} rules`}</h2>
      <ol>{game.rules[lang].map(rule => <li key={rule}>{rule}</li>)}</ol>
      <h2>{t('tips')}</h2>
      <ul>{game.tips[lang].map(tip => <li key={tip}>{tip}</li>)}</ul>
    </section>
    <section className="game-faq">
      <h2>{lang === 'zh' ? '常见问题' : 'Frequently asked questions'}</h2>
      {game.faq[lang].map(item => <details key={item.question}><summary>{item.question}</summary><p>{item.answer}</p></details>)}
    </section>
    <p className="editorial-note">{game.released?(lang === 'zh' ? '本页游戏、说明和策略由 PlayFree 独立制作并测试。无需下载或注册，游戏记录仅保存在当前浏览器。' : 'This game, guide and strategy were independently created and tested by PlayFree. No download or account is required; scores stay in this browser.'):(lang==='zh'?'本页是 PlayFree 对经典规则的独立网页实现；界面、代码与说明由本站制作并测试。无需下载或注册。':'This page is PlayFree’s independent browser implementation of classic rules; its interface, code and guide were built and tested here. No download or account is required.')}</p>
    <h2 className="section-title">{t('more')}</h2><GameGrid category={game.category} excludeId={game.id} limit={4} />
  </article>
}
